export const POW_ATTEMPT_LIMIT = 5_000_000
export const POW_DIFFICULTY_MAX = 18

export const CONTROL_EXAMPLE = {
  difficulty: 12,
  ticketId: '11111111-1111-4111-8111-111111111111',
  nonce: '0707070707070707070707070707070707070707070707070707070707070707',
  mobile: '79991234567',
  counter: '3190',
  publicKey: 'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAED6+w7r+Shy3fQoOyCBAEcwNaIHIS+AKBRgzEFqDCjP6/fJelu8XUICuWo6VrDCCRMfSq3laaCeq/+zPNkOPCqA==',
  signature: '0jeTZopizxLqBF4fdTCeMUMAHf/UUkFJ6Rvhhj0wDwuB36PXKBaX7b10LozCtRtVGiobT0PpewZU2Q7qe9G4UQ==',
  puzzleHash: '0001f5dcd9e33229e1305dac036faf1fae1d95d720b8758928f1a6d368e1e0ed',
}

export type T_AuthCaptchaChallenge = Record<string, unknown>

export type T_AuthSecurity = {
  request_id: string
  ticket_id: string
  nonce: string
  counter: number
  public_key: string
  signature: string
}

export type T_AuthAttempt = {
  mobile: string
  operationId: string
  ticketId: string
  nonce: string
  counter: string
  publicKey: string
  signature: string
  privateKey: CryptoKey
}

export const createPaidOperationId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16)
    const value = char === 'x' ? random : ((random & 0x3) | 0x8)

    return value.toString(16)
  })
}

export const isWebCryptoAvailable = () => (
  typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined'
)

export const isAllowedDifficulty = (difficulty: number) => (
  Number.isInteger(difficulty) && difficulty >= 1 && difficulty <= POW_DIFFICULTY_MAX
)

export const toCanonicalRuMobile = (input: string) => {
  let digits = input.replace(/\D/g, '')

  if (digits.length === 11 && digits.startsWith('8')) {
    digits = `7${digits.slice(1)}`
  } else if (digits.length === 10 && digits.startsWith('9')) {
    digits = `7${digits}`
  }

  if (!/^7\d{10}$/.test(digits)) return null

  return digits
}

export const readCaptchaChallenge = (data: unknown): T_AuthCaptchaChallenge | null => {
  if (!data || typeof data !== 'object') return null

  const challenge = (data as { captcha_challenge?: unknown }).captcha_challenge
  if (!challenge || typeof challenge !== 'object' || Array.isArray(challenge)) return null

  return challenge as T_AuthCaptchaChallenge
}

export const puzzleText = (nonce: string, mobile: string, counter: string) => (
  `${nonce}|${mobile}|${counter}`
)

export const authSignPayload = (parts: {
  ticketId: string
  nonce: string
  mobile: string
  counter: string
  publicKey: string
}) => [
  'era-auth-v1',
  parts.ticketId,
  parts.nonce,
  parts.mobile,
  parts.counter,
  parts.publicKey,
].join('\n')

export const hasLeadingZeroBits = (hash: Uint8Array, difficulty: number) => {
  let left = difficulty
  let index = 0

  while (left >= 8) {
    if (hash[index] !== 0) return false
    index += 1
    left -= 8
  }

  if (left === 0) return true

  const mask = (0xff << (8 - left)) & 0xff
  return (hash[index] & mask) === 0
}

const bytesToHex = (bytes: Uint8Array) => (
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
)

export const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = ''

  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index])
  }

  return btoa(binary)
}

export const base64ToBytes = (value: string) => {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  return bytes
}

const toArrayBuffer = (bytes: Uint8Array) => {
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return copy.buffer
}

export const puzzleHashHex = async (nonce: string, mobile: string, counter: string) => {
  const bytes = new TextEncoder().encode(puzzleText(nonce, mobile, counter))
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
  return bytesToHex(hash)
}

export const solvePow = async (
  nonce: string,
  mobile: string,
  difficulty: number,
  attemptLimit = POW_ATTEMPT_LIMIT,
) => {
  if (!isAllowedDifficulty(difficulty)) return null

  const encoder = new TextEncoder()

  for (let counter = 0; counter < attemptLimit; counter += 1) {
    const bytes = encoder.encode(puzzleText(nonce, mobile, String(counter)))
    const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))

    if (hasLeadingZeroBits(hash, difficulty)) return String(counter)
  }

  return null
}

export const generateAuthKeyPair = async () => {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    [ 'sign', 'verify' ],
  )
  const spki = await crypto.subtle.exportKey('spki', keyPair.publicKey)

  return {
    privateKey: keyPair.privateKey,
    publicKey: bytesToBase64(new Uint8Array(spki)),
  }
}

export const signAuthPayload = async (privateKey: CryptoKey, payload: string) => {
  const raw = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    new TextEncoder().encode(payload),
  )

  return bytesToBase64(new Uint8Array(raw))
}

export const verifyAuthSignature = async (
  publicKeyBase64: string,
  payload: string,
  signatureBase64: string,
) => {
  const publicKey = await crypto.subtle.importKey(
    'spki',
    toArrayBuffer(base64ToBytes(publicKeyBase64)),
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    [ 'verify' ],
  )

  return crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    publicKey,
    toArrayBuffer(base64ToBytes(signatureBase64)),
    new TextEncoder().encode(payload),
  )
}
